import { useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { parsePesosToCents, formatCents, MESES } from '@/features/catalog/catalog';
import { Boton, Campo, CONTROL, EncabezadoPantalla } from '@/ui/Sistema';
import { crearOrganizacion, type AltaDeVeterinaria } from './organizations';
import type { MobilityMode } from '@/features/clients/clients';

/**
 * El primer día de alguien que acaba de instalar la aplicación.
 *
 * ## Por qué existe
 *
 * Quien se registra no pertenece a ninguna veterinaria, y sin veterinaria la
 * aplicación entera queda muda: cada pantalla se dibuja vacía porque no hay
 * organización de la que traer datos. Antes esa persona se quedaba mirando un
 * tablero cuyos seis botones no llevaban a ningún lado.
 *
 * ## Por qué es un asistente y no una pantalla de configuración
 *
 * Lo que hay que definir antes del primer trabajo está repartido en cinco
 * pantallas distintas —participantes, movilidad, repartos, precios,
 * tarifario—, y cada una supone que ya se entiende el modelo. Recorrerlas sin
 * conocer la aplicación es adivinar en qué orden.
 *
 * Acá se pregunta en el orden en que se piensa el trabajo, una cosa por vez, y
 * se explica para qué sirve cada respuesta. Ninguna es irreversible: son los
 * mismos valores que después se editan en Configuración.
 *
 * ## Lo que NO se pregunta
 *
 * Todo lo que se puede deducir o dejar para después. Trabajando solo no se
 * pregunta el reparto —es todo suyo— ni quién factura. El tarifario viene con
 * veinticuatro conceptos ya cargados. Y el precio del novillo se puede saltear:
 * sin él los trabajos se cargan igual y el importe queda PENDIENTE, que es
 * honesto, mientras que pedirlo como obligatorio frenaría el alta de alguien
 * que todavía no lo tiene a mano.
 */
interface Props {
  onListo: () => void;
}

type Paso = 'nombre' | 'equipo' | 'movilidad' | 'precio';

const MODOS: { valor: MobilityMode; rotulo: string; ayuda: string }[] = [
  {
    valor: 'per_km_fuel',
    rotulo: 'Por kilómetro, según el combustible',
    ayuda: 'Se calcula con el precio del combustible que cargues cada mes.',
  },
  {
    valor: 'per_km_fixed',
    rotulo: 'Por kilómetro, a una tarifa fija',
    ayuda: 'Vos ponés cuánto vale el kilómetro.',
  },
  {
    valor: 'flat',
    rotulo: 'Un monto fijo por viaje',
    ayuda: 'El mismo importe, sin importar la distancia.',
  },
  {
    valor: 'manual',
    rotulo: 'Lo escribo en cada trabajo',
    ayuda: 'Sin cálculo automático: se carga a mano cada vez.',
  },
];

export function Bienvenida({ onListo }: Props) {
  const [paso, setPaso] = useState<Paso>('nombre');
  const [error, setError] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);

  const [nombre, setNombre] = useState('');
  const [quien, setQuien] = useState('');
  const [solo, setSolo] = useState<boolean | null>(null);
  const [cobraMovilidad, setCobraMovilidad] = useState<boolean | null>(null);
  const [modo, setModo] = useState<MobilityMode>('per_km_fuel');
  const [tarifa, setTarifa] = useState('');
  const [novillo, setNovillo] = useState('');
  const [combustible, setCombustible] = useState('');

  const hoy = new Date();

  const crear = async () => {
    setError(null);
    setCreando(true);
    try {
      const datos: AltaDeVeterinaria = {
        name: nombre.trim(),
        kind: solo ? 'solo' : 'team',
        my_display_name: quien.trim(),
        charge_mobility: cobraMovilidad === true,
        mobility_mode: cobraMovilidad ? modo : 'none',
        mobility_rate_cents:
          cobraMovilidad && modo === 'per_km_fixed' ? parsePesosToCents(tarifa) : null,
        flat_mobility_cents: cobraMovilidad && modo === 'flat' ? parsePesosToCents(tarifa) : null,
        steer_price_cents: parsePesosToCents(novillo),
        fuel_price_cents: parsePesosToCents(combustible),
        price_year: hoy.getFullYear(),
        price_month: hoy.getMonth() + 1,
      };
      await crearOrganizacion(datos);
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setCreando(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-5 p-5" data-testid="bienvenida">
      <EncabezadoPantalla
        titulo="Empecemos"
        nota="Cuatro preguntas y ya podés cargar tu primer trabajo. Todo esto se cambia después."
      />

      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="bienvenida-error">
          {error}
        </p>
      )}

      {/* --- 1. Quién sos y cómo se llama la veterinaria --- */}
      {paso === 'nombre' && (
        <section className="flex flex-col gap-4" data-testid="paso-nombre">
          <Campo rotulo="¿Cómo se llama tu veterinaria?">
            <input
              className={CONTROL}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Veterinaria del Sur"
              data-testid="campo-nombre"
            />
          </Campo>

          <Campo
            rotulo="¿Y vos cómo te llamás?"
            ayuda="Es el nombre con el que vas a figurar en los repartos y las liquidaciones."
          >
            <input
              className={CONTROL}
              value={quien}
              onChange={(e) => setQuien(e.target.value)}
              placeholder="Manuel"
              data-testid="campo-quien"
            />
          </Campo>

          <Boton
            jerarquia="primario"
            disabled={nombre.trim() === '' || quien.trim() === ''}
            onClick={() => setPaso('equipo')}
            data-testid="siguiente-nombre"
          >
            Seguir
          </Boton>
        </section>
      )}

      {/* --- 2. Solo o en equipo --- */}
      {paso === 'equipo' && (
        <section className="flex flex-col gap-3" data-testid="paso-equipo">
          <p className="text-sm">¿Trabajás solo o son varios?</p>
          {/* Cambia mucho más que un rótulo: trabajando solo, el reparto es
              todo suyo y se deja configurado. En equipo NO se inventa ninguno,
              porque quién se lleva qué es lo que hay que acordar entre
              personas, y suponerlo sería decidir sobre la plata de otro. */}
          <Opcion
            elegida={solo === true}
            titulo="Trabajo solo"
            ayuda="Todo lo que factures y ganes es tuyo. Después podés invitar a alguien."
            onClick={() => setSolo(true)}
            testId="opcion-solo"
          />
          <Opcion
            elegida={solo === false}
            titulo="Somos varios"
            ayuda="Vas a poder invitar a los demás y armar cómo se reparte cada trabajo."
            onClick={() => setSolo(false)}
            testId="opcion-equipo"
          />
          <Boton
            jerarquia="primario"
            disabled={solo === null}
            onClick={() => setPaso('movilidad')}
            data-testid="siguiente-equipo"
          >
            Seguir
          </Boton>
        </section>
      )}

      {/* --- 3. La movilidad --- */}
      {paso === 'movilidad' && (
        <section className="flex flex-col gap-3" data-testid="paso-movilidad">
          <p className="text-sm">¿Cobrás la movilidad?</p>
          <Opcion
            elegida={cobraMovilidad === true}
            titulo="Sí, la cobro"
            ayuda="Se suma al trabajo además de los honorarios."
            onClick={() => setCobraMovilidad(true)}
            testId="opcion-cobra"
          />
          <Opcion
            elegida={cobraMovilidad === false}
            titulo="No la cobro"
            ayuda="Los trabajos llevan sólo honorarios."
            onClick={() => setCobraMovilidad(false)}
            testId="opcion-no-cobra"
          />

          {cobraMovilidad && (
            <div className="flex flex-col gap-3 border-t border-[var(--linea)] pt-3">
              <p className="text-sm">¿Cómo la calculás?</p>
              {MODOS.map((m) => (
                <Opcion
                  key={m.valor}
                  elegida={modo === m.valor}
                  titulo={m.rotulo}
                  ayuda={m.ayuda}
                  onClick={() => setModo(m.valor)}
                  testId={`modo-${m.valor}`}
                />
              ))}

              {(modo === 'per_km_fixed' || modo === 'flat') && (
                <Campo
                  rotulo={modo === 'per_km_fixed' ? 'Precio del kilómetro' : 'Monto por viaje'}
                >
                  <input
                    className={CONTROL}
                    inputMode="decimal"
                    value={tarifa}
                    onChange={(e) => setTarifa(e.target.value)}
                    placeholder="900"
                    data-testid="campo-tarifa"
                  />
                </Campo>
              )}
            </div>
          )}

          <Boton
            jerarquia="primario"
            disabled={cobraMovilidad === null}
            onClick={() => setPaso('precio')}
            data-testid="siguiente-movilidad"
          >
            Seguir
          </Boton>
        </section>
      )}

      {/* --- 4. Los precios del mes --- */}
      {paso === 'precio' && (
        <section className="flex flex-col gap-4" data-testid="paso-precio">
          <Campo
            rotulo={`Precio del kilo de novillo en ${MESES[hoy.getMonth()]}`}
            ayuda="Los honorarios se cargan en kilos de novillo y se convierten con este precio."
          >
            <input
              className={CONTROL}
              inputMode="decimal"
              value={novillo}
              onChange={(e) => setNovillo(e.target.value)}
              placeholder="4300"
              data-testid="campo-novillo"
            />
          </Campo>

          {cobraMovilidad && modo === 'per_km_fuel' && (
            <Campo rotulo="Precio del litro de combustible">
              <input
                className={CONTROL}
                inputMode="decimal"
                value={combustible}
                onChange={(e) => setCombustible(e.target.value)}
                placeholder="850"
                data-testid="campo-combustible"
              />
            </Campo>
          )}

          {/* Se puede saltear a propósito. Sin precio, los trabajos se cargan
              igual y el importe queda PENDIENTE —que es cierto— en vez de
              frenar el alta de alguien que todavía no lo tiene a mano. */}
          <p className="text-xs text-[var(--tinta-suave)]">
            Si todavía no lo sabés, seguí igual: vas a poder cargar trabajos y los importes van a
            quedar pendientes hasta que lo completes en Precios.
          </p>

          <Boton
            jerarquia="primario"
            disabled={creando}
            onClick={() => void crear()}
            data-testid="crear-veterinaria"
          >
            {creando ? 'Creando…' : 'Crear mi veterinaria'}
          </Boton>
        </section>
      )}

      {/* Un resumen de lo contestado, para no tener que recordar qué se puso
          tres pasos atrás. */}
      {paso !== 'nombre' && (
        <div className="flex flex-col gap-1 border-t border-[var(--linea)] pt-3 text-xs text-[var(--tinta-suave)]">
          <span data-testid="resumen-alta">
            {nombre} · {quien}
            {solo !== null && (solo ? ' · trabaja solo' : ' · equipo')}
            {cobraMovilidad !== null &&
              (cobraMovilidad ? ' · cobra movilidad' : ' · sin movilidad')}
            {novillo.trim() !== '' && ` · novillo ${formatCents(parsePesosToCents(novillo))}`}
          </span>
          <button
            type="button"
            className="self-start underline"
            onClick={() =>
              setPaso(paso === 'precio' ? 'movilidad' : paso === 'movilidad' ? 'equipo' : 'nombre')
            }
            data-testid="volver-paso"
          >
            Volver
          </button>
        </div>
      )}
    </div>
  );
}

/** Una opción grande, para el pulgar y con su explicación al lado. */
function Opcion({
  elegida,
  titulo,
  ayuda,
  onClick,
  testId,
}: {
  elegida: boolean;
  titulo: string;
  ayuda: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={elegida}
      className={`flex min-h-14 flex-col gap-0.5 rounded-[var(--radius-control)] border p-3 text-left ${
        elegida ? 'border-[var(--accion)] bg-[var(--superficie)]' : 'border-[var(--linea-fuerte)]'
      }`}
      data-testid={testId}
    >
      <span className="font-medium">
        {elegida ? '✓ ' : ''}
        {titulo}
      </span>
      <span className="text-xs text-[var(--tinta-suave)]">{ayuda}</span>
    </button>
  );
}
